import Image from "next/image";
import { LanguageFlag } from "@/types/types";
import { FaInstagram, FaFacebookF, FaStar } from "react-icons/fa";

interface UserCardProps {
  id: string;
  name: string;
  location?: string;
  role?: string | string[];
  avatarUrl?: string | null;
  languages?: string[];
  socials?: {
    instagram?: string | null;
    facebook?: string | null;
  };
  isSupporter: boolean;
}

export default function UserSimpleCard({
  id,
  name,
  location,
  role,
  avatarUrl,
  languages,
  socials,
  isSupporter,
}: UserCardProps) {
  const fallbackInitial = name?.charAt(0).toUpperCase() ?? "?";
  const avatarSrc = avatarUrl || `/images/avatar/${id}.jpg`;
  const useFallback = !avatarUrl;

  return (
    <div
      className={`bg-white rounded-xl ${isSupporter
          ? "border-2 border-[#FAD564] shadow-md shadow-[#FAD564]/40"
          : "border border-transparent shadow"
        } overflow-hidden w-full max-w-sm flex flex-col`}
    >
      <div className="relative bg-[#D3DC7C] h-20">
        {isSupporter && (
          <div className="absolute top-1 right-1 sm:top-4 sm:right-4 flex items-center gap-1 bg-[#FAD564] text-[#1B475D] text-xs font-semibold px-4 py-2 rounded-full shadow">
            <FaStar size={14} />
            Top Pick
          </div>
        )}
        <div className="absolute inset-x-0 top-9 flex justify-center">
          <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white bg-white flex items-center justify-center">
            {useFallback ? (
              <div className="w-full h-full bg-[#8EBD9D] text-white font-bold text-3xl flex items-center justify-center rounded-full">
                {fallbackInitial}
              </div>
            ) : (
              <Image
                src={avatarSrc}
                alt={name}
                width={100}
                height={100}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center text-center mt-10 px-5 flex-grow">
        <h2 className="text-xl text-[#1B475D] font-bold min-h-[56px] flex items-center justify-center text-center">
          {name}
        </h2>

        {location && <p className="text-sm text-gray-700 mt-1">{location}</p>}

        {(Array.isArray(role) && role.length > 0) || role ? (
          <div className="min-h-[55px] flex flex-wrap justify-center items-center gap-2">
            {Array.isArray(role) ? (
              role.map((r, index) => (
                <span
                  key={index}
                  className="min-h-[25px] bg-[#1B475D] text-white text-sm font-semibold px-3 py-1 rounded-full inline-flex items-center justify-center text-center"
                >
                  {r}
                </span>
              ))
            ) : (
              <span className="min-h-[25px] bg-[#1B475D] text-white text-sm font-semibold px-3 py-1 rounded-full inline-flex items-center justify-center text-center">
                {role}
              </span>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-center gap-3 mt-1">
        {/* Flags */}
        <div className="flex items-center gap-1">
          {languages?.map((lang) => {
            const src = LanguageFlag[lang as keyof typeof LanguageFlag];
            return src ? (
              <Image
                key={lang}
                src={src}
                alt={`${lang} flag`}
                width={20}
                height={14}
                className="rounded-sm object-cover w-[20px] h-[14px] border border-gray-300"
              />
            ) : (
              <span key={lang} className="text-xs text-gray-500">
                {lang}
              </span>
            );
          })}
        </div>

        {/* Divider */}
        {(socials?.instagram || socials?.facebook) && (
          <span className="text-gray-400">|</span>
        )}

        <div className="flex items-center gap-3 min-h-[24px]">
          {socials?.instagram && (
            <div aria-label="Instagram" className="hover:text-[#8EBD9D]">
              <FaInstagram size={18} />
            </div>
          )}
          {socials?.facebook && (
            <div aria-label="Facebook" className="hover:text-[#8EBD9D]">
              <FaFacebookF size={18} />
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4">
        <button className="w-full bg-[#8ebd9d] hover:bg-[#77A885] text-[#1B475D] text-sm font-semibold py-2 px-4 rounded transition">
          View Profile
        </button>
      </div>
    </div>
  );
}
